/**
 * Estado do `ProbabilitySlot` (§7.14 bloco 3; nome de FE §5.4, inferência). Pura e pequena: o integrador a importa
 * estaticamente no slot da coluna esquerda (chunk `index`) para escolher entre o `TrendChart` sozinho e o
 * `ProbabilitySlot` (chunk `probability`, por `import()`), sem baixar o chunk só para decidir (AC-17.2-20).
 *
 * Ponto de extensão (PE-13, §14.6): aqui não há cálculo de probabilidade. O estado sai do bloco `probability` que a
 * vista já traz depois das guardas de `selectView` (`displayProbability`, §14.6.4); as guardas abaixo repetem as que
 * decidem a exibição, por segurança (camada dupla, como a trava E01 de §16.5).
 *
 * `features/` não importa `config` (§2.2.2): a configuração entra pela forma estrutural `SlotConfig`, que `AppConfig`
 * satisfaz.
 */
import type { ProbabilityPayload } from '../../contracts/probability';
import { PROBABILITY_STALE_MS, PROBABILITY_STALE_SEQS } from '../../model/probability';
import { epochMs, T_OPEN, tMinOfIso } from '../../model/time';
import type { LayoutState, ResultsView } from '../../model/types';

/** §7.14 bloco 3 (`detail` do P2 fica de fora: o histograma não é desta fase). */
export type ProbabilitySlotState =
  | { kind: 'hidden' }
  /** `status = 'waiting_data'` ou instante exibido antes das 17h00; `reason` = `public_reason` (catálogo P-04). */
  | { kind: 'waiting'; reason: string | null }
  | { kind: 'active'; p: ProbabilityPayload }
  /** `status = 'paused'` ou payload velho (guarda de idade de §14.6.4). */
  | { kind: 'paused'; reason: string | null };

export type ProbabilitySlotKind = ProbabilitySlotState['kind'];

/** O que o estado lê da configuração (`AppConfig` satisfaz). */
export interface SlotConfig {
  features: { probability: boolean };
  election_date: string;
}

/** Guarda de idade (§14.6.4 regra 5): `published_at − generated_at > 180 s` (o mesmo valor de `displayProbability`). */
export const STALE_AFTER_MS = PROBABILITY_STALE_MS;
/** Guarda de idade (§14.6.4 regra 5): `base_seq < seq − 3`. */
export const STALE_SEQS = PROBABILITY_STALE_SEQS;

/** Payload velho em relação à vista (§14.6.4 regra 5). */
export function isStale(p: ProbabilityPayload, v: Pick<ResultsView, 'publishedAt' | 'seq'>): boolean {
  const age = Date.parse(v.publishedAt) - Date.parse(p.generated_at);
  return age > STALE_AFTER_MS || p.base_seq < v.seq - STALE_SEQS;
}

/** E01 (§16.5): snapshot publicado antes das 17h00 de Brasília do dia da eleição, pela hora do SERVIDOR. */
export function publishedBeforeOpen(v: Pick<ResultsView, 'publishedAt'>, electionDate: string): boolean {
  const t = tMinOfIso(v.publishedAt, epochMs(electionDate));
  return !(t >= T_OPEN);
}

/**
 * A faixa da chance no cartão principal (`ProbabilityStrip`, C01): as mesmas regras do slot, menos as de tamanho de
 * tela; nunca na TV. Devolve o payload ativo ou null. Desde a chance em foco (DECISOES §71), a faixa só aparece onde a
 * chance em foco não cabe (celular deitado, computador estreito `tiny`) e enquanto o chunk dela não chega.
 */
export function stripActive(
  cfg: SlotConfig,
  v: ResultsView | null,
  mode: LayoutState['mode'],
  displayTMin: number,
): ProbabilityPayload | null {
  if (mode === 'tv') return null;
  const s = probabilitySlotState(cfg, v, { mode: 'flow', heightClass: 'tall' }, displayTMin);
  return s.kind === 'active' ? s.p : null;
}

/**
 * As regiões onde a chance em foco pode montar: `right` (topo da coluna direita), `hero` (dentro do cartão principal) e
 * `top` (cartão próprio logo acima do herói; só no arranjo A, ORD-F-151).
 */
export type ChanceFocusRegion = 'right' | 'hero' | 'top';

/**
 * Arranjo da chance na tela da noite (ORD-F-151; a trava mora em app/chanceLayout.ts, que `features/` não importa):
 * `today` (DECISOES §71), `A` (palco: cartão próprio no topo) e `B` (dois lados: o herói dividido).
 */
export type ChanceArrangement = 'today' | 'A' | 'B';

/**
 * Onde mora a chance em foco (§7.14 R1 nova; decisão do responsável de 18/10 ~23h10, ORD-F-099 item 1; DECISOES §71):
 * - `split` (computador largo, palco ≥ 1280; ORD-F-112): monta nas duas regiões, e o CSS mostra uma só, pela altura
 *   lógica do palco (`ProbabilitySlot.css`, media query de altura e proporção): palco alto → topo da coluna direita;
 *   palco < 1100 lógicos (todo laptop e toda tela 16:9, 16:10 e 3:2) → dentro do cartão principal. Nada de medir conteúdo por JS;
 * - `hero`: dentro do cartão principal, logo depois da diferença (computador estreito, tablet e celular em pé);
 * - null: não cabe acima da dobra (TV, celular deitado, computador estreito `tiny`); fica a faixa compacta de hoje.
 * `wide` vem do integrador (`isWide` de app/layoutMode; `features/` não importa `app/`).
 */
export type ChanceFocusPlace = ChanceFocusRegion | 'split';

export function chanceFocusPlace(
  layout: Pick<LayoutState, 'mode' | 'heightClass'>,
  wide: boolean,
  arrangement: ChanceArrangement = 'today',
): ChanceFocusPlace | null {
  if (layout.mode === 'tv' || layout.mode === 'land') return null;
  if (layout.mode === 'desktop') {
    if (layout.heightClass === 'tiny' && !wide) return null;
    // A (ORD-F-151): no computador largo, o palco no topo da coluna direita, em qualquer altura (o feed vai para abas)
    if (wide) return arrangement === 'A' ? 'right' : arrangement === 'B' ? 'hero' : 'split';
    return arrangement === 'A' ? 'top' : 'hero';
  }
  return arrangement === 'A' ? 'top' : 'hero';
}

/**
 * A chance em foco aparece neste estado? `today` (ORD-F-104 item 5): só ativa ou em pausa. A e B (ORD-F-151): também em
 * espera, desde que o instante exibido já seja 17h00 ou depois (nada projetivo antes das 17h00, nem o aviso).
 */
export function chanceFocusShows(
  state: ProbabilitySlotState,
  displayTMin: number,
  arrangement: ChanceArrangement = 'today',
): boolean {
  if (state.kind === 'hidden') return false;
  if (state.kind !== 'waiting') return true;
  return arrangement !== 'today' && displayTMin >= T_OPEN;
}

/**
 * Estado da chance em foco: o do slot (flag, 17h00 pela hora do servidor e pelo instante exibido, definição matemática
 * ou Eleito · TSE, espera, pausa, guarda de idade), sem as regras de tamanho de tela (o lugar é `chanceFocusPlace`).
 */
export function chanceFocusState(cfg: SlotConfig, v: ResultsView | null, displayTMin: number): ProbabilitySlotState {
  return probabilitySlotState(cfg, v, { mode: 'flow', heightClass: 'tall' }, displayTMin);
}

/**
 * §7.14 bloco 3, na ordem da seção, mais a trava E01 pela hora do servidor (AC-16.5-01: com `published_at` de 16h59 o
 * slot não existe; com 17h00, existe). `displayTMin` é o instante exibido (`timeline.displayTMin`).
 */
export function probabilitySlotState(
  cfg: SlotConfig,
  v: ResultsView | null,
  layout: Pick<LayoutState, 'mode' | 'heightClass'>,
  displayTMin: number,
): ProbabilitySlotState {
  const p = v?.probability ?? null;
  if (!cfg.features.probability || !v || !p) return { kind: 'hidden' };
  if (layout.mode === 'tv' || layout.mode === 'tablet' || layout.mode === 'land') return { kind: 'hidden' };
  if (layout.mode === 'desktop' && (layout.heightClass === 'short' || layout.heightClass === 'tiny')) {
    return { kind: 'hidden' };
  }
  // a chance sai de cena com a presidência definida (matemática ou TSE)
  if (v.national.mathDecision || v.national.tseResult) return { kind: 'hidden' };
  if (p.status === 'off' || p.status === 'error' || p.status === 'ended') return { kind: 'hidden' };
  if (publishedBeforeOpen(v, cfg.election_date)) return { kind: 'hidden' };
  if (displayTMin < T_OPEN || p.status === 'waiting_data') return { kind: 'waiting', reason: p.public_reason };
  if (p.status === 'paused' || isStale(p, v)) return { kind: 'paused', reason: p.public_reason };
  // `active` sem os números do contrato para os dois candidatos (win_prob e final_share só com active) é tratado como
  // pausa: nunca um placar vazio nem "–%" no lugar da chance
  if (!hasNumbers(p, v)) return { kind: 'paused', reason: p.public_reason };
  return { kind: 'active', p };
}

/** `win_prob` e `final_share` finitos para os dois números da disputa (ordem do contrato). */
function hasNumbers(p: ProbabilityPayload, v: ResultsView): boolean {
  if (p.win_prob === null || p.final_share === null) return false;
  for (const c of v.candidates) {
    const w = p.win_prob[c.number];
    const q = p.final_share[c.number];
    if (typeof w !== 'number' || !Number.isFinite(w) || !q) return false;
    if (![q.q10, q.q50, q.q90].every((x) => typeof x === 'number' && Number.isFinite(x))) return false;
  }
  return true;
}

/** Slide inicial (§7.14 bloco 4): "Projeção" (1) só no estado ativo; senão "Ao longo da apuração" (0). */
export function initialSlide(kind: ProbabilitySlotKind): 0 | 1 {
  return kind === 'active' ? 1 : 0;
}
